/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import cx from 'classnames';

import { Icon, toTitleCase, InventoryTag } from 'sarsaparilla';

import type {
    LocationSuggestionOptionType,
    Suggestion,
    ContentSuggestion,
    InventorySuggestion,
    LocationSuggestion,
    HistorySuggestion,
    EntityTypeSuggestion,
} from './LocationSuggestion.types';

import styles from './LocationSuggestion.module.scss';

const TEXT_SEARCH_NEAR_ME = 'Search for places or activities near me';

export function isContentSuggestion(
    suggestion: Suggestion
): suggestion is ContentSuggestion {
    return (suggestion as ContentSuggestion).parent_name !== undefined;
}

export function isInventorySuggestion(
    suggestion: Suggestion
): suggestion is InventorySuggestion {
    return (suggestion as InventorySuggestion).is_inventory !== undefined;
}

export function isLocationSuggestion(
    suggestion: Suggestion
): suggestion is LocationSuggestion {
    return (suggestion as LocationSuggestion).lat !== undefined;
}

export function isHistorySuggestion(
    suggestion: Suggestion
): suggestion is HistorySuggestion {
    return (suggestion as HistorySuggestion).last_used !== undefined;
}

export function isEntityTypeSuggestion(
    suggestion: Suggestion
): suggestion is EntityTypeSuggestion {
    return (
        isContentSuggestion(suggestion) ||
        isInventorySuggestion(suggestion) ||
        isHistorySuggestion(suggestion)
    );
}

export function getSuggestionValue(suggestion: LocationSuggestionOptionType): string {
    let suggestionText = suggestion.text;
    if (isInventorySuggestion(suggestion)) {
        // if suggestion is associated with an inventory name, then capitalize the name to clean it up
        suggestionText = toTitleCase(suggestionText);
    }

    return suggestionText;
}

export function getSuggestionIcon(suggestion: LocationSuggestionOptionType) {
    if (
        (isContentSuggestion(suggestion) ||
            isInventorySuggestion(suggestion) ||
            isHistorySuggestion(suggestion)) &&
        suggestion.entity_type
    ) {
        switch (suggestion.entity_type) {
            case 'campground': {
                if (
                    isInventorySuggestion(suggestion) &&
                    suggestion?.campsite_type_of_use?.length === 1 &&
                    suggestion?.campsite_type_of_use[0] === 'Day'
                ) {
                    return <InventoryTag.InventoryDayUse isSpanItem isInverse />;
                }
                return <InventoryTag.InventoryCamping isSpanItem isInverse />;
            }

            case 'ticketfacility':
            case 'tour':
            case 'timedentry':
            case 'timedentry_tour':
                return (
                    <InventoryTag.InventoryTicket
                        isSpanItem
                        isInverse
                        className="tour-suggestion-icon"
                    />
                );

            case 'permit':
            case 'vehiclepermit':
                return <InventoryTag.InventoryPermit isSpanItem isInverse />;

            case 'recarea':
                return (
                    <InventoryTag.InventoryRecArea
                        isSpanItem
                        isInverse
                        className="rec-area-suggestion-icon"
                    />
                );

            case 'pass':
                return (
                    <InventoryTag.InventorySitePass
                        isSpanItem
                        isInverse
                        className="rec-area-suggestion-icon"
                    />
                );

            case 'activitypass':
                return (
                    <InventoryTag.InventoryActivityPass
                        isSpanItem
                        isInverse
                        className="rec-area-suggestion-icon"
                    />
                );

            case 'treepermit':
                return (
                    <InventoryTag.InventoryTreePermits
                        isSpanItem
                        isInverse
                        className="rec-area-suggestion-icon"
                    />
                );

            case 'interagencypass':
                return (
                    <InventoryTag.InventoryInteragencyPass
                        isSpanItem
                        isInverse
                        className="rec-area-suggestion-icon"
                    />
                );

            case 'venuereservations':
            case 'venuereservations_venue':
                return <InventoryTag.InventoryVenueReservations isSpanItem isInverse />;
            case 'program':
            case 'program_session':
                return <InventoryTag.InventoryPrograms isSpanItem isInverse />;
            case 'kb':
                return (
                    <div className={cx(styles.nonInventoryIcon, styles.helpIcon)}>
                        <Icon iconName="help-outline" />
                    </div>
                );
            case 'cms':
            case 'page':
                return (
                    <div className={cx(styles.nonInventoryIcon, styles.articleIcon)}>
                        <Icon iconName="description" />
                    </div>
                );
            default:
                return <InventoryTag.InventoryPOI isSpanItem isInverse />;
        }
    }

    if (isLocationSuggestion(suggestion) && !isInventorySuggestion(suggestion)) {
        return <InventoryTag.InventoryMapPin isSpanItem isInverse />;
    }

    if (
        isHistorySuggestion(suggestion) &&
        !suggestion.entity_type &&
        suggestion.country_code
    ) {
        return <InventoryTag.InventoryMapPin isSpanItem isInverse />;
    }

    if (suggestion.text === TEXT_SEARCH_NEAR_ME) {
        return <InventoryTag.InventoryMapPin isSpanItem isInverse />;
    }

    if (
        !isLocationSuggestion(suggestion) &&
        !isInventorySuggestion(suggestion) &&
        suggestion.text
    ) {
        return (
            <div className={styles.nonInventoryIcon}>
                <Icon iconName="history" />
            </div>
        );
    }

    return <InventoryTag.InventoryPOI className="poi-suggestion-icon" />;
}

// cSpell:ignore ticketfacility, timedentry, vehiclepermit, recarea, activitypass, treepermit, interagencypass, venuereservations
